import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowLeft } from "@phosphor-icons/react";

const TITLES = {
  terms:   "Terms of Service",
  privacy: "Privacy Policy",
  refund:  "Refund Policy",
};

export default function LegalPage() {
  const { slug } = useParams();
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/legal/${slug}`);
        setMd(data.markdown || "");
      } catch (err) {
        setError(extractApiError(err, "Document not found"));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`legal-${slug}`}>
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="text-overline text-primary mb-3">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-8">{TITLES[slug] || "Legal"}</h1>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {error && <div className="text-sm text-destructive">{error}</div>}
        {md && <MarkdownView text={md} />}
        <div className="mt-16 pt-8 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-6">
          <Link to="/legal/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/legal/refund" className="hover:text-foreground">Refund</Link>
        </div>
      </div>
    </div>
  );
}

/** Very small, safe markdown-ish renderer for h1/h2/h3, bullets, bold, paragraphs. */
function MarkdownView({ text }) {
  // Strip the leading '# Title' since we already show the title above
  const cleaned = text.replace(/^#\s+.+\n+/, "");
  const blocks = cleaned.split(/\n\n+/);
  return (
    <article className="prose prose-slate max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h2:text-primary prose-p:leading-relaxed prose-p:text-sm prose-li:text-sm prose-strong:text-foreground">
      {blocks.map((b, i) => renderBlock(b, i))}
    </article>
  );
}

function renderBlock(block, i) {
  const b = block.trim();
  if (!b) return null;
  if (b.startsWith("## ")) return <h2 key={i}>{b.slice(3)}</h2>;
  if (b.startsWith("### ")) return <h3 key={i}>{b.slice(4)}</h3>;
  if (b.split("\n").every((l) => l.startsWith("- ") || l.startsWith("* "))) {
    return (
      <ul key={i}>
        {b.split("\n").map((l, j) => <li key={j}>{inline(l.replace(/^[-*]\s+/, ""))}</li>)}
      </ul>
    );
  }
  return <p key={i}>{inline(b)}</p>;
}

function inline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
}
